import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { QRCodeSVG } from 'qrcode.react';
import { FaRss, FaGlobeAmericas, FaMapMarkerAlt } from 'react-icons/fa'
import { designTokens } from '@/styles/designTokens'

const NewsContainer = styled(motion.div)`
  background-color: ${designTokens.colors.neutral.medium};
`
const NewsList = styled.div`
  flex: 1;
  overflow: hidden;
  position: relative;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const NewsItem = styled(motion.div)`
  background: ${designTokens.colors.gradients.card};
  border: 1px solid ${designTokens.colors.neutral.border};
  border-radius: ${designTokens.borderRadius.lg};
  padding: ${designTokens.spacing.lg};
  border-left: 4px solid ${props => props.categoryColor};
  transition: ${designTokens.transitions.normal};
  cursor: pointer;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: stretch;
  flex: 1 1 auto;
  min-height: 0;
  min-width: 0;
  box-shadow: ${designTokens.shadows.sm};
  
  &:hover {
    background: ${designTokens.colors.neutral.backgroundHover};
    transform: translateY(-2px);
    box-shadow: ${designTokens.shadows.md};
  }
`;

const NewsCategories = styled.div`
  display: flex;
  flex-wrap: nowrap;
  gap: ${designTokens.spacing.sm};
  margin-bottom: ${designTokens.spacing.sm};
  overflow-x: auto;
  white-space: nowrap;
  align-items: center;
  max-width: 100%;
`;

const NewsCategory = styled.span`
  background: ${props => props.color}20;
  color: ${props => props.color};
  padding: ${designTokens.spacing.xs} ${designTokens.spacing.sm};
  border-radius: ${designTokens.borderRadius.sm};
  font-weight: ${designTokens.typography.fontWeight.medium};
  font-size: ${designTokens.typography.fontSize.sm};
  font-family: ${designTokens.typography.fontFamily.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  flex: 1 1 0%;
  min-width: 0;
`;

const NewsTitleRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  min-width: 0;
`;

const NewsTitleText = styled.h4`
  color: ${designTokens.colors.text.primary};
  font-size: ${designTokens.typography.fontSize.xl};
  font-weight: ${designTokens.typography.fontWeight.semibold};
  font-family: ${designTokens.typography.fontFamily.primary};
  margin: 0;
  line-height: ${designTokens.typography.lineHeight.tight};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  flex: 1 1 0%;
  min-width: 0;
  width: 0;
  text-align: left;
`;

const NewsQRCode = styled.div`
  margin-right: ${designTokens.spacing.lg};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 118px;
  height: 118px;
  flex-shrink: 0;
`;

const NewsDescription = styled.p`
  color: ${designTokens.colors.text.secondary};
  font-size: ${designTokens.typography.fontSize.base};
  font-family: ${designTokens.typography.fontFamily.primary};
  margin: ${designTokens.spacing.sm} 0 0 0;
  line-height: ${designTokens.typography.lineHeight.normal};
  max-height: 3.2em;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  text-align: left;
`;

const NewsMeta = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${designTokens.spacing.sm};
  font-size: ${designTokens.typography.fontSize.sm};
  color: ${designTokens.colors.text.muted};
  flex-shrink: 0;
  margin-top: ${designTokens.spacing.xs};
`;

const NewsSource = styled.span`
  background: ${designTokens.colors.primary.blue}20;
  color: ${designTokens.colors.primary.blue};
  padding: ${designTokens.spacing.xs} ${designTokens.spacing.sm};
  font-weight: ${designTokens.typography.fontWeight.medium};
  font-size: ${designTokens.typography.fontSize.sm};
  font-family: ${designTokens.typography.fontFamily.primary};
  white-space: nowrap;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${designTokens.borderRadius.sm};
  margin-bottom: 10px;
`;

const NoNewsMessage = styled.div`
  color: ${designTokens.colors.text.muted};
  text-align: center;
  padding: ${designTokens.spacing['2xl']} ${designTokens.spacing.lg};
  font-size: ${designTokens.typography.fontSize.base};
  font-family: ${designTokens.typography.fontFamily.primary};
`;

// Função para limpar descrição: remove nome do veículo, datas e autores
function cleanDescription(desc, source) {
  if (!desc) return '';
  let text = desc.trim();
  // Remove nome do veículo no início
  if (source && text.toLowerCase().startsWith(source.toLowerCase())) {
    text = text.slice(source.length).replace(/^[:\-–—\s]+/, '');
  }
  // Remove datas no início (ex: 11/07/2025, 11/07/2025 14:30, etc)
  text = text.replace(/^\d{1,2}\/\d{1,2}\/\d{2,4}(\s+\d{1,2}[:h]\d{1,2})?\s*-?\s*/i, '');
  // Remove "Por Nome" ou "por Nome" no início
  text = text.replace(/^(por|Por)\s+[A-ZÁÉÍÓÚÃÕÂÊÎÔÛÇ][^\-–—:]{2,40}([\-–—:])?\s*/i, '');
  return text.trim();
}

const NewsPanel = ({ news = [] }) => {
  const [currentNewsIndex, setCurrentNewsIndex] = useState(0);
  const [visibleNews, setVisibleNews] = useState([]);

  const categoryColors = {
    tecnologia: '#00d4ff',
    industria: '#4CAF50',
    economia: '#FFD700',
    energia: '#FF9800',
    politica: '#E91E63',
    jundiai: '#2196F3',
    regiao: '#90caf9', // azul claro para melhor legibilidade
    geral: '#9C27B0'
  };

  const categoryNames = {
    tecnologia: 'Tecnologia',
    industria: 'Indústria',
    economia: 'Economia',
    energia: 'Energia',
    politica: 'Política',
    jundiai: 'Jundiaí',
    regiao: 'Região',
    geral: 'Geral'
  };

  useEffect(() => {
    if (news.length > 0) {
      setVisibleNews(news.slice(0, 1));
    }
  }, [news]);

  useEffect(() => {
    if (news.length > 1) {
      const interval = setInterval(() => {
        setCurrentNewsIndex((prev) => {
          const nextIndex = (prev + 1) % news.length;
          setVisibleNews(news.slice(nextIndex, nextIndex + 1));
          return nextIndex;
        });
      }, 24000);

      return () => clearInterval(interval);
    }
  }, [news]);

  const handleNewsClick = (link) => {
    if (link && link !== '#') {
      window.open(link, '_blank');
    }
  };

  if (!news || news.length === 0) {
    return (
      <NewsContainer>
        <NoNewsMessage>
          Carregando notícias...
        </NoNewsMessage>
      </NewsContainer>
    );
  }

  return (
    <NewsContainer>
      <NewsList>
        <AnimatePresence mode="wait">
          {visibleNews.map((item, index) => (
            <NewsItem
              key={`${item.title}-${index}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
              categoryColor={categoryColors[item.category] || categoryColors.geral}
              onClick={() => handleNewsClick(item.link)}
            >
              <div style={{display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', width: '100%'}}>
              <NewsQRCode>
                  {item.link && item.link !== '#' && (
                    <QRCodeSVG value={item.link} size={280} fgColor="#ffffff" bgColor="transparent" level="L" />
                  )}
              </NewsQRCode>
                <div style={{display: 'flex', flexDirection: 'column', alignItems: 'left', justifyContent: 'space-between', width: '100%'}}>
                  {/* categorias numa linha acima do título */}
                  <div style={{display: 'flex', flexDirection: 'row', alignItems: 'left',  width: '100%', gap: '8px'}}>
                  <NewsSource>{item.source}</NewsSource>
                  <NewsCategories>
                      {Array.isArray(item.category)
                        ? item.category.map((cat) => (
                            <NewsCategory
                              key={cat}
                              color={categoryColors[cat] || categoryColors.geral}
                            >
                              {categoryNames[cat] || cat}
                            </NewsCategory>
                          ))
                        : (
                            <NewsCategory color={categoryColors[item.category] || categoryColors.geral}>
                              {categoryNames[item.category] || item.category || 'Geral'}
                            </NewsCategory>
                          )}
                    </NewsCategories>
                    </div>
                  <NewsTitleRow> 
                    <NewsTitleText title={item.title}>{item.title}</NewsTitleText>
                  </NewsTitleRow>
                  {item.description && (
                    <NewsDescription title={item.description}>
                      {cleanDescription(item.description, item.source)}
                    </NewsDescription>
                  )}
                  </div>
              </div>
            </NewsItem>
          ))}
        </AnimatePresence>
      </NewsList>
    </NewsContainer>
  );
};

export default NewsPanel; 